import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { AlertCircle, ArrowRight, Check } from "lucide-react";
import { siteData } from "../../data/mock";

const spring = { type: "spring", damping: 80, stiffness: 400, mass: 1 };

export const ProblemSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { problem } = siteData;

  return (
    <section ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Text */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...spring, delay: 0.1 }}
              className="text-[#0A0A0A] leading-tight tracking-tight mb-8"
              style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 500 }}
            >
              {problem.headline}
              <br />
              <span className="text-[#D98A8C]">{problem.headlineAccent}</span>
            </motion.h2>

            <div className="space-y-4 mb-8">
              {problem.points.map((point, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ ...spring, delay: 0.2 + i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-[#D98A8C]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle size={14} className="text-[#D98A8C]" />
                  </div>
                  <p className="text-[#3C3B3D] text-base leading-relaxed">{point}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right - Transition Statement */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ ...spring, delay: 0.4 }}
            className="relative"
          >
            <div className="bg-[#F0EDF5] rounded-3xl p-8 md:p-12">
              <p
                className="text-[#0A0A0A] leading-snug mb-4"
                style={{ fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 500 }}
              >
                {problem.transition}
              </p>
              <div className="w-12 h-1 bg-[#008055] rounded-full" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const SolutionSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { solution } = siteData;

  return (
    <section ref={ref} className="py-20 md:py-28 bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ ...spring, delay: 0.2 }}
            className="relative order-2 lg:order-1"
          >
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <img
                src={solution.image}
                alt="Business owner using P2Points"
                className="w-full h-[320px] md:h-[420px] object-cover"
              />
            </div>
            {/* Overlay card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="absolute -bottom-4 -right-4 md:bottom-6 md:right-6 bg-white rounded-2xl p-4 shadow-lg border border-[#E6E4EB]"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#008055]/10 flex items-center justify-center">
                  <Check size={18} className="text-[#008055]" />
                </div>
                <div>
                  <p className="text-[#0A0A0A] text-sm font-medium">Ready in Days</p>
                  <p className="text-[#757478] text-xs">Not months</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <div className="order-1 lg:order-2">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...spring, delay: 0.1 }}
              className="text-[#0A0A0A] leading-tight tracking-tight mb-8"
              style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 500 }}
            >
              {solution.headline}
              <span className="text-[#008055]">{solution.headlineAccent}</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...spring, delay: 0.2 }}
              className="text-[#3C3B3D] text-lg leading-relaxed mb-8"
            >
              With P2Points, you get everything you need to run a modern loyalty program:
            </motion.p>

            <div className="space-y-4 mb-8">
              {solution.items.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ ...spring, delay: 0.3 + i * 0.08 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-[#008055]/10 flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-[#008055]" />
                  </div>
                  <p className="text-[#0A0A0A] text-base font-medium">{item}</p>
                </motion.div>
              ))}
            </div>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...spring, delay: 0.6 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                const el = document.querySelector("#final-cta");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-[#008055] hover:bg-[#0A6647] text-white border-none rounded-full px-6 h-12 text-base font-normal cursor-pointer flex items-center gap-2 transition-colors duration-200"
            >
              Get Started
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};
